import type { FilterRuleModel } from '@/shared/generated'
import type { OptimizationGroupsType } from './helpers'

import { useMemo } from 'react'
import { any, prop } from 'lodash/fp'

import { useBIAnalyticQuery, useBIRevisionQuery } from '@/entities/bi'
import { useSelectedCurrency } from '@/entities/currency'
import { useInterpretabilityStatisticQuery } from '@/entities/optimization-statistic'

import { Operators } from '@/shared/components/query-builder'

import { getPeriod, PROGRESS_TAB_METRICS } from './helpers'

export const useOptimizationsProgressAnalytics = ({
	filters,
	optimizationGroups,
	enabled = true,
}: {
	filters: FilterRuleModel[]
	optimizationGroups: OptimizationGroupsType[]
	enabled?: boolean
}) => {
	const currency = useSelectedCurrency()
	const optimizationGroupIds = useMemo(
		() => optimizationGroups?.map(({ id }) => id),
		[optimizationGroups]
	)
	const biRevisionDateQuery = useBIRevisionQuery<string>({
		refetchOnMount: 'always',
		select: (data) => data?.['date'] as string,
	})
	const interpretabilityStatisticQuery = useInterpretabilityStatisticQuery(
		{
			optimizations: optimizationGroups.map(({ id, optimization }) => ({
				optimization_id: optimization,
				optimization_group_id: id,
			})),
			filters,
			currency,
		},
		{
			enabled: !!optimizationGroups && !!enabled,
		}
	)
	const biAnalyticQuery = useBIAnalyticQuery(
		{
			date_aggregation: 'date_key',
			metrics: PROGRESS_TAB_METRICS,
			product_filters: [
				{
					name: 'optimization_group_id',
					operation: Operators.IN,
					value: optimizationGroupIds,
				},
				...filters,
			],
			filters: [],
			currency,
			...getPeriod(biRevisionDateQuery.data),
		},
		{
			enabled:
				!!biRevisionDateQuery.data && !!optimizationGroupIds && !!enabled,
		}
	)
	const isLoading = any(prop('isLoading'), [
		biRevisionDateQuery,
		biAnalyticQuery,
	])
	const isError = any(prop('isError'), [
		biRevisionDateQuery,
		biAnalyticQuery,
		interpretabilityStatisticQuery,
	])

	return {
		isLoading,
		isError,
		data: {
			biAnalytic: biAnalyticQuery.data,
			revisionDate: biRevisionDateQuery.data,
			interpretabilityStatistic: interpretabilityStatisticQuery.data,
		},
	}
}
